/* eslint-disable eqeqeq */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { usePrivateLayout } from '~/layouts/private/providers/privateLayout';

import { useToast } from './toast';
import { useAuth } from './auth';
import customAxios from '../services/customAxios';
import { IFCWithChildren } from '../interfaces/IFCWithChildren';
import { Modal } from '../components/Modal';
import { ConfirmationModal } from '../components/ConfirmationModal';

export type TFavoritedCoin = {
  name: string;
  image: string;
  acronym: string;
  id: number;
};

interface IFavoritesCoinsContextData {
  toggleFavorite: (id: number) => void;
  switchVariant: (newVariant: 'crypto' | 'stock') => void;
  favorites: TFavoritedCoin[];
  favoritesIds: number[];
  variant: 'crypto' | 'stock';
}

const FavoritesCoinsContext = createContext({} as IFavoritesCoinsContextData);

const FavoritesCoinsProvider: IFCWithChildren = ({ children }) => {
  const { showToast } = useToast();
  const { isMobile } = usePrivateLayout();
  const { user } = useAuth();
  const [favorites, setFavorites] = useState<TFavoritedCoin[]>([]);
  const [removing, setRemoving] = useState<string | null>(null);
  const [variant, setVariant] = useState<'crypto' | 'stock'>('crypto');

  const switchVariant = useCallback((newVariant: 'crypto' | 'stock') => {
    setVariant(newVariant);
  }, []);

  const load = useCallback(async () => {
    if (user) {
      try {
        const path = variant === 'crypto' ? '/Favorito' : '/Favorito/stocks';
        const { data: response } = await customAxios.get(path);
        setFavorites(
          ((response as any[]) || []).map((fav) => ({
            id: fav.id,
            name: fav.nome,
            acronym: fav.sigla,
            image: fav.imagemUrl64,
          }))
        );
      } catch {
        showToast({
          type: 'error',
          description: 'Houve um erro ao carregar as moedas favoritas',
        });
      }
    }
  }, [showToast, variant, user]);

  const removeFavorite = useCallback(async () => {
    const path =
      variant === 'crypto'
        ? `/Favorito/${removing}`
        : `/Favorito/stocks/${removing}`;

    await customAxios.delete(path);
    setRemoving(null);
    load();
  }, [load, variant, removing]);

  const toggleFavorite = useCallback(
    async (id: number) => {
      const exists = favorites.find((where) => where.id == id);
      if (exists) {
        setRemoving(String(id));
      } else {
        const path =
          variant === 'crypto' ? `/Favorito/${id}` : `/Favorito/stocks/${id}`;
        await customAxios.post(path);
        load();
      }
    },
    [favorites, variant, load]
  );

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant]);

  return (
    <FavoritesCoinsContext.Provider
      value={{
        favorites,
        variant,
        favoritesIds: (favorites || []).map((fav) => fav.id),
        switchVariant,
        toggleFavorite,
      }}
    >
      <Modal
        id="remove-favorite-modal"
        isOpen={!!removing}
        onClose={() => setRemoving(null)}
        title="Remover favorito"
        height={isMobile ? '181px' : 'auto'}
        width="400px"
      >
        <ConfirmationModal
          onAbort={() => setRemoving(null)}
          onConfirm={removeFavorite}
          text={
            variant === 'crypto'
              ? 'Confirma a remoção da moeda favorita?'
              : 'Confirma a remoção da ação favorita?'
          }
        />
      </Modal>
      {children}
    </FavoritesCoinsContext.Provider>
  );
};

const useFavoritesCoins = (): IFavoritesCoinsContextData => {
  return useContext(FavoritesCoinsContext);
};

export { FavoritesCoinsProvider, useFavoritesCoins };
